import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useQueryClient } from '@tanstack/react-query';
import { TEST_OVERVIEW } from '@elementary/common-ui/constants/queryKeys';
import { PARENT_TEST_ID } from '@elementary/common-ui/constants/urlParams';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { gql } from 'graphql-request';
import { useLocation } from 'react-router-dom';
import { TestOverview } from '../types';

const isGqlNotFoundError = (error: unknown): boolean =>
  (error as any)?.response?.errors?.some(
    (e: any) => e?.extensions?.code === 'NOT_FOUND',
  ) ?? false;

export const getLatestTestExecutionQuery = gql`
  query GetLatestTestExecution($id: String!) {
    latestTestExecution(id: $id) {
      test {
        id
        name
        type
        description
        owners: unifiedOwners
        subscribers: unionedSubscribers
        tags
        disabled
        tableAsset {
          id
          name
          tags
          lastDataModificationTime
        }
      }
      execution {
        id
        subType
        qualityDimension
        failures
        resource {
          type
          name
        }
        testId
        subTestUniqueId
        status
        executionTimestamp
      }
    }
  }
`;

const getAllTestExecutionsQuery = gql`
  query GetAllTestExecutions($id: String!) {
    latestTestExecutions(filters: { testIds: [$id] }) {
      test {
        id
        name
        type
        description
        owners: unifiedOwners
        subscribers: unionedSubscribers
        tags
        disabled
        tableAsset {
          id
          name
          tags
          lastDataModificationTime
        }
      }
      execution {
        id
        subType
        qualityDimension
        failures
        resource {
          type
          name
        }
        testId
        subTestUniqueId
        status
        executionTimestamp
      }
    }
  }
`;

function useTestOverviewData() {
  const { selectedId, selectedDrawerType } = useSidePanelParams();
  const queryClient = useQueryClient();
  const selectedEnvId = useSelectedEnv();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const parentTestId = searchParams.get(PARENT_TEST_ID);
  const isTestManagementContext =
    location.pathname.includes('/test-management') && !parentTestId;
  const useAggregatedQuery =
    isTestManagementContext && selectedDrawerType !== 'cloud_test';

  const mainQueryKey = [TEST_OVERVIEW, selectedId, selectedEnvId, 'main'];
  const allExecQueryKey = [TEST_OVERVIEW, selectedId, selectedEnvId, 'all'];

  const isTestDrawer =
    selectedDrawerType === 'test' || selectedDrawerType === 'cloud_test';

  const { data, isLoading, isFetching, error } = useGraphqlQuery<{
    latestTestExecution: TestOverview;
  }>({
    queryKey: mainQueryKey,
    query: getLatestTestExecutionQuery,
    variables: { id: selectedId },
    enabled: !!selectedId && isTestDrawer,
  });

  const { data: allExecData } = useGraphqlQuery<{
    latestTestExecutions: TestOverview[];
  }>({
    queryKey: allExecQueryKey,
    query: getAllTestExecutionsQuery,
    variables: { id: selectedId },
    enabled: useAggregatedQuery && !!selectedId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries(mainQueryKey);
    queryClient.invalidateQueries(allExecQueryKey);
  };

  const processedData: TestOverview | undefined = (
    data as { latestTestExecution?: TestOverview }
  )?.latestTestExecution;

  const allExecutions = allExecData?.latestTestExecutions;

  // For dbt tests in test management context (no parentTestId), NOT_FOUND means the test
  // has no execution history yet. Treat as empty data so the empty state is shown instead
  // of an error panel. (Cloud tests return null instead of NOT_FOUND — handled above.)
  const effectiveError =
    useAggregatedQuery && isGqlNotFoundError(error) ? null : error;

  return {
    isLoading,
    isFetching,
    data: processedData,
    allExecutions,
    error: effectiveError,
    invalidate,
  };
}

export default useTestOverviewData;
